import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Stack,
    Autocomplete,
    TextField,
    Popover,
    Snackbar,
    Alert,
    Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthIcon from "../../../assets/images/Calendar.svg";
import dayjs from "dayjs";
import CustomButton from "../buttons/CustomButton";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import styles from "./DateRangePicker.module.css";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const getMonthNumber = (monthName) => {
    const index = months.indexOf(monthName);
    return index === -1 ? "01" : String(index + 1).padStart(2, "0");
};

// Get min date (1 month before today)
const getMinDate = () => {
    return dayjs().subtract(1, 'month');
};

// Get max date (1 month after today)
const getMaxDate = () => {
    return dayjs().add(1, 'month');
};

const generateYears = () => {
    const minDate = getMinDate();
    const maxDate = getMaxDate();
    const yearsArray = [];

    for (let y = minDate.year(); y <= maxDate.year(); y++) {
        yearsArray.push(`${y}`);
    }

    return yearsArray;
};

const DateSelector = ({ label, date, setDate, disabled = false, isFromDate = false }) => {
    const selectedYear = parseInt(date.year);
    const selectedMonthIndex = months.indexOf(date.month);
    const minDate = getMinDate();
    const maxDate = getMaxDate();

    // Filter allowed months based on min/max date constraints
    const allowedMonths = useMemo(() => {
        let filtered = [...months];

        if (selectedYear === minDate.year()) {
            filtered = months.slice(minDate.month());
        }

        if (selectedYear === maxDate.year()) {
            filtered = filtered.slice(0, maxDate.month() + 1);
        }

        return filtered;
    }, [selectedYear, minDate, maxDate]);

    const allowedDays = useMemo(() => {
        if (selectedYear && selectedMonthIndex >= 0) {
            const monthNum = String(selectedMonthIndex + 1).padStart(2, '0');
            const daysInMonth = dayjs(`${selectedYear}-${monthNum}-01`).daysInMonth();
            let validDays = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString().padStart(2, "0"));

            // Filter days based on min date constraint
            if (selectedYear === minDate.year() && selectedMonthIndex === minDate.month()) {
                validDays = validDays.filter(day => parseInt(day) >= minDate.date());
            }

            // Filter days based on max date constraint
            if (selectedYear === maxDate.year() && selectedMonthIndex === maxDate.month()) {
                validDays = validDays.filter(day => parseInt(day) <= maxDate.date());
            }

            return validDays;
        }
        return ["01"];
    }, [selectedYear, selectedMonthIndex, minDate, maxDate]);

    const availableYears = generateYears();

    return (
        <Box style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "11px", fontWeight: "500", color: "#2B2B2B", paddingBottom: "8px" }}>{label}</span>
            <Stack direction="row" spacing={1}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #949495',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        minWidth: '100%',
                        height: '30%',
                        pointerEvents: disabled ? 'none' : 'auto',
                        opacity: disabled ? 0.5 : 1,
                    }}
                >
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={allowedDays}
                        value={allowedDays.includes(date.day) ? date.day : allowedDays[0]}
                        onChange={(_, value) => setDate(prev => ({ ...prev, day: value }))}
                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 50, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 100,
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="01" InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                    <Divider orientation="vertical" flexItem sx={{ color: "#949495" }} />
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={allowedMonths}
                        value={allowedMonths.includes(date.month) ? date.month : allowedMonths[0]}
                        onChange={(_, value) => {
                            const selectedYear = parseInt(date.year);
                            const selectedMonthIndex = months.indexOf(value);
                            const monthNum = String(selectedMonthIndex + 1).padStart(2, '0');
                            const daysInMonth = dayjs(`${selectedYear}-${monthNum}-01`).daysInMonth();

                            setDate(prev => ({
                                ...prev,
                                month: value,
                                day: parseInt(prev.day) > daysInMonth ? "01" : prev.day
                            }));
                        }}
                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 120, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 120,
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Month" InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                    <Divider orientation="vertical" flexItem />
                    <Autocomplete
                        size="small"
                        disableClearable
                        disabled={disabled}
                        options={availableYears}
                        value={availableYears.includes(date.year) ? date.year : availableYears[0]}
                        onChange={(_, value) => setDate(prev => ({ ...prev, year: value }))}
                        popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: '12px', pointerEvents: 'none' }} />}
                        sx={{ width: 70, '& .MuiOutlinedInput-root': { border: 'none', borderRadius: 0, '& fieldset': { border: 'none' } } }}
                        ListboxProps={{
                            style: {
                                maxHeight: 120,
                                fontSize: '12px',
                                padding: 0,
                            }
                        }}
                        PaperComponent={({ children }) => (
                            <div style={{
                                margin: 0,
                                fontSize: '12px',
                                background: '#ffffff',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                                padding: 0,
                            }}>{children}</div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} placeholder={`${dayjs().year()}`} InputProps={{
                                ...params.InputProps,
                                sx: { ...params.InputProps?.sx, fontSize: '10px', fontWeight: '400', color: '#000' },
                            }} inputProps={{
                                ...params.inputProps,
                                style: { ...(params.inputProps?.style || {}), fontSize: '12px' },
                            }} />
                        )}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

const formatDisplayDate = (dateObj) => {
    if (!dateObj.day || !dateObj.month || !dateObj.year) return "";
    const monthNum = getMonthNumber(dateObj.month);
    const formattedDate = dayjs(`${dateObj.year}-${monthNum}-${dateObj.day}`);
    return formattedDate.isValid() ? formattedDate.format("DD MMMM YYYY") : "";
};

const DateRangePickerFilter = ({ label, value, onChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const minDate = getMinDate();
    const maxDate = getMaxDate();

    // Initialize with default dates (1 month before today to today)
    const [fromDate, setFromDate] = useState(value?.fromDate || {
        day: minDate.format('DD'),
        month: months[minDate.month()],
        year: String(dayjs().year())
    });

    const [toDate, setToDate] = useState(value?.toDate || {
        day: dayjs().format('DD'),
        month: months[dayjs().month()],
        year: String(dayjs().year())
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");

    const fromFormatted = useMemo(() => formatDisplayDate(fromDate), [fromDate]);
    const toFormatted = useMemo(() => formatDisplayDate(toDate), [toDate]);

    const handleSetDates = () => {
        const from = dayjs(`${fromDate.year}-${getMonthNumber(fromDate.month)}-${fromDate.day}`);
        const to = dayjs(`${toDate.year}-${getMonthNumber(toDate.month)}-${toDate.day}`);

        if (!from.isValid() || !to.isValid()) {
            setSnackbarMsg("Invalid dates selected.");
            setSnackbarOpen(true);
            return;
        }

        if (to.isBefore(from)) {
            setSnackbarMsg("To date cannot be earlier than From date.");
            setSnackbarOpen(true);
            return;
        }

        if (from.isBefore(minDate, 'day')) {
            setSnackbarMsg(`From date cannot be before ${minDate.format('DD MMMM YYYY')}.`);
            setSnackbarOpen(true);
            return;
        }

        if (to.isAfter(maxDate, 'day')) {
            setSnackbarMsg(`To date cannot be after ${maxDate.format('DD MMMM YYYY')}.`);
            setSnackbarOpen(true);
            return;
        }

        // Pass valid date range to parent
        onChange({
            from: from.format('YYYY-MM-DD'),
            to: to.format('YYYY-MM-DD'),
            fromDate: fromDate,
            toDate: toDate
        });

        setAnchorEl(null);
    };

    useEffect(() => {
        if (value?.fromDate) setFromDate(value.fromDate);
        if (value?.toDate) setToDate(value.toDate);
    }, [value]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column'}}>
            <label className={styles.label}>{label}</label>
            <Box>
                <Box
                    onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                    }}
                    sx={{
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        px: 1,
                        minWidth: "220px",
                        width: "auto",
                        cursor: "pointer",
                        display: "flex",
                        height: "37px",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#ffffff",
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <img alt="Calendar" src={CalendarMonthIcon} style={{ width: "7%", height: "7%" }} />
                        <Stack>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#949495' }}>From date</span>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#2B2B2B', whiteSpace: 'nowrap' }}>{fromFormatted || "Select From Date"}</span>
                        </Stack>
                        <Stack>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#949495' }}>To date</span>
                            <span style={{ fontSize: '10px', fontWeight: '400', color: '#2B2B2B', whiteSpace: 'nowrap' }}>{toFormatted || "Select To Date"}</span>
                        </Stack>
                    </Stack>
                    {/* <KeyboardArrowDownIcon fontSize="10px" fontWeight="500" /> */}
                </Box>

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    PaperProps={{ sx: { p: 0, borderRadius: 2, boxShadow: 3, width: 280, height: 250 } }}
                >
                    <Box sx={{ backgroundColor: "#005EEF33", p: 1, borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                        <span style={{ fontSize: '11px', fontWeight: '400', display: 'flex', justifyContent: 'center' }}>Date range</span>
                    </Box>
                    <Box sx={{ p: 2 }}>
                        <DateSelector label="From" date={fromDate} setDate={setFromDate} isFromDate={true} />
                        <Box mt={2}>
                            <DateSelector label="To" date={toDate} setDate={setToDate} />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", pt: 1 }}>
                            <CustomButton label="Done" type="date" onClick={handleSetDates} />
                        </Box>
                    </Box>
                </Popover>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
                        {snackbarMsg}
                    </Alert>
                </Snackbar>
            </Box>
        </div>

    );
};

export default DateRangePickerFilter;
